import { canCallOut } from '@/config/navigation';
import { useCompaniesList } from '@/hooks/use-company/use-company';
import { useUser } from '@/hooks/use-user/use-user';
import { PlanTier, UserRole } from '@/services/user-service/user.service.types';
import { Navigate } from 'react-router-dom';

type RoleBasedRouteProps = {
  children: React.ReactNode;
  allowedRoles?: UserRole[];
  deniedRoles?: UserRole[];
  allowedPlanTiers?: PlanTier[];
  requireConversationalAgent?: boolean;
  requireOutboundCalls?: boolean;
};

export const RoleBasedRoute = ({
  children,
  allowedRoles,
  deniedRoles,
  allowedPlanTiers,
  requireConversationalAgent,
  requireOutboundCalls,
}: RoleBasedRouteProps) => {
  const { userData } = useUser();
  const { companies, isPending: companiesPending } = useCompaniesList();

  if (!userData) {
    return <Navigate to="/sign-in" replace />;
  }

  const userRoles = userData.roles.map((role) => role.name);

  if (deniedRoles && deniedRoles.some((role) => userRoles.includes(role))) {
    return <Navigate to="/" replace />;
  }

  if (allowedRoles && !allowedRoles.some((role) => userRoles.includes(role))) {
    return <Navigate to="/" replace />;
  }

  if (allowedPlanTiers && !allowedPlanTiers.includes(userData.plan_tier)) {
    return <Navigate to="/" replace />;
  }

  if (requireConversationalAgent && userData.conversational_agent === false) {
    return <Navigate to="/" replace />;
  }

  if (requireOutboundCalls) {
    // A deep link lands before the companies do, and bouncing it then would be for nothing.
    if (companiesPending) return null;
    if (!canCallOut(companies)) return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};
